import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { TypedMemo } from '../../common/types';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { useCustomerPortalUrl } from '../../common/hooks/useCustomerPortalUrl';
import { LoadingView } from '../../common/views/LoadingView';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useTeam } from '../../team/useTeam';
import { SubscriptionMemberPlan } from './SubscriptionMemberPlan';
import { SwitchToHomeWorkspace } from '../../workspaces/SwitchToHomeWorkspace';
import { SubscriptionPlanCard } from './SubscriptionPlanCard';
import { SubscriptionFeaturesList } from './SubscriptionFeaturesList';
import { SubscriptionTrialDifferences } from './SubscriptionTrialDifferences';
import { MonthlyAddOnsSection } from './addOns/MonthlyAddOnsSection';

/**
 * Design r1–r3: the plan card (name, status chip, dates, actions) and "What your plan includes"
 * — every `MUSIC24_FEATURE_ROWS` row, grouped, with usage where a client endpoint counts it.
 */
const SubscriptionOwnerViewComponent: React.FC = () => {
  const dispatch = useDispatch();
  const licenseInfo = useLicenseInfo();
  const isActive = licenseInfo.isSubscriptionValid();
  const { portalUrl, isLoading: isPortalLoading } = useCustomerPortalUrl({ enabled: true });

  // `licenseInfo.validLicense` is only set for an active *paid* subscription (`license.isValid`),
  // so it's null for a Free user — this tab needs the plan's capabilities regardless of validity.
  // `useMusic24Capabilities` reads `licenseInfo.license.smlCapabilities` (the same object
  // the trial pill reads the plan from) and defaults any missing field from the Free row, so it's
  // never null.
  const capabilities = useMusic24Capabilities();

  const openPricing = useCallback(() => {
    dispatch(toggleSMLPricingModal({ shouldShow: true }));
  }, [dispatch]);

  return (
    <div>
      <SubscriptionPlanCard
        capabilities={capabilities}
        isActive={isActive}
        trialEndsAt={licenseInfo.trialEndsAt}
        trialDaysRemaining={licenseInfo.trialDaysRemaining}
        trialLimitsUntil={licenseInfo.trialLimitsUntil}
        renewalDate={licenseInfo.subscriptionExpiresAt}
        portalUrl={portalUrl}
        isPortalLoading={isPortalLoading}
        onOpenPricing={openPricing}
      />

      {/* QA skp-8: during the trial, every way it differs from the plan whose limits it gives. */}
      {capabilities.plan === 'trial' && <SubscriptionTrialDifferences />}

      {/* design: monthly-addons a1-a4, b2-b8: extra posts and video credits on the web plan. */}
      <div className="mb-4">
        <MonthlyAddOnsSection />
      </div>

      <SubscriptionFeaturesList capabilities={capabilities} />
    </div>
  );
};

const SubscriptionOwnerView = TypedMemo(SubscriptionOwnerViewComponent);

/**
 * Only the owner manages billing (ruling 8). The owner view calls owner-only billing procedures,
 * so a member must never mount it — this wrapper decides from `team.list` before either view
 * renders, instead of the owner view failing loudly on an owner-only refusal.
 */
const SubscriptionTabComponent: React.FC = () => {
  const team = useTeam();

  if (team.isLoading) {
    return <LoadingView />;
  }

  if (team.data?.myRole === 'MEMBER') {
    return (
      <>
        {/* design: workspaces f1 / f2: the plan of the workspace, who manages it, and the way to the home workspace's billing. */}
        <SubscriptionMemberPlan />
        {/* design: monthly-addons b1: the member reads the add-ons and sees whom to ask. */}
        <div className="my-4">
          <MonthlyAddOnsSection />
        </div>
        <SwitchToHomeWorkspace />
      </>
    );
  }

  return <SubscriptionOwnerView />;
};

export const SubscriptionTab = TypedMemo(SubscriptionTabComponent);
